import React, {ChangeEvent, memo, useEffect, useMemo, useRef, useState} from "react"
import {Button, Header, Icon} from "semantic-ui-react"
import {Navigate, Route, Routes} from "react-router-dom"
import {ErrorBoundary} from "@sentry/react"
import {ErrorBoundaryChecker} from "./error/error-boundary"
import {TopBar} from "./menu/top-bar"
import {FormattedMessage} from "react-intl"
import {ScreenSize} from "./utils/media-utils"
import {ErrorPage} from "./error/error-page"
import {toChart} from "./converter/create-gedcom-backend"
import {useLocation, useNavigate} from "react-router"
import {ChartType, TopolaChart} from "./chart"
import {AppState} from "./app"
import {IndiInfo} from "./topola"
import {getSelection} from "./datasource/load-data"
import {DEFAULT_CONFIG, EthnicityArg, IdsArg, LanguagesArg, SexArg} from "./config"
import {startIndi, TopolaData} from "./utils/gedcom-utils"
import queryString, {ParsedQuery} from "query-string"
import {getArguments} from "./utils/param-utils"
import {getFilename} from "./utils/chart-utils"
import {GedcomLanguage as Language} from "./model/gedcom-language"
import {analyticsEvent} from "./utils/google-analytics"
import {DataSourceSpecs, SourceSelection, UploadedDataSource, UploadSourceSpec} from "./datasource/data-source-specs"
import LanguagesLoader from "./datasource/load-languages"
import {CheckoutSuccess} from "./api/checkout-success"
import {CheckoutCancelled} from "./api/checkout-cancelled"
import * as Sentry from "@sentry/react"
import "./styles/mobile.css"


const landingElements = ["mobile-header", "mobile-image", "mobile-text", "upload-ged", "error-boundary"]

const allowedExtensions = [".ged", ".gdz", ".gedzip"]

interface AppProps { setI18nLanguage: (lang: string) => void }

export function App(props: AppProps) {
    const onI18nLanguage = props.setI18nLanguage
    const [allLanguages, setAllLanguages] = useState<Language[]>([])

    const [appState, setAppState] = useState(AppState.INITIAL)
    const [sourceSpec, setSourceSpec] = useState<DataSourceSpecs>()
    const [config, setConfig] = useState(DEFAULT_CONFIG)
    const [selection, setSelection] = useState<IndiInfo>()
    const [freezeAnimation, setFreezeAnimation] = useState(false)
    const [topolaData, setTopolaData] = useState<TopolaData>()

    const [contactModalOpen, setContactModalOpen] = useState<boolean>(false)
    const [pricingModalOpen, setPricingModalOpen] = useState<boolean>(false)
    const [checkoutSuccess, setCheckoutSuccess] = useState(false)
    const [privacyModalOpen, setPrivacyModalOpen] = useState<boolean>(false)
    const [errorBoundaryCheck, setErrorBoundaryCheck] = useState(false)

    const TopMenu = memo(TopBar)

    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [file, setFile] = useState<File | null>()
    const location = useLocation()
    const navigate = useNavigate()

    useEffect(() => {
        analyticsEvent("mobile_landing")
        loadLanguages().catch(e => console.error("Failed to load languages:", e))
        loadDataFromArgs().catch(e => console.error("Failed to load data:", e))
    })

    useEffect(() => {
        if (!file) return
        setAppState(AppState.LOADING)
        ;(async () => {
            const extra = await file.text()
            console.log(`Uploaded '${file.name}' valid. Loading chart...`)
            Sentry.captureMessage(`[MOBILE] Uploaded '${file.name}' valid.`, { level: "log", extra: { file: extra }})
            await loadDataFromArgs()
            await toChart(file, navigate)
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file])

    async function loadLanguages() {
        try {
            const allLanguages = await LanguagesLoader.loadLanguages("data/languages/languages.csv") || []
            setAllLanguages(allLanguages)
        } catch (e) {
            console.error("Failed to load languages:", e)
        }
    }

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            await handleFile(event.target.files[0])
        }
    }

    const handleFile = async (newFile: File) => {
        // Only accept GEDCOM file types
        if (!allowedExtensions.includes(newFile.name.toLowerCase().slice(newFile.name.lastIndexOf(".")))) {
            Sentry.captureMessage("Invalid file type", {level: "error", extra: { file: newFile.name }})
            return
        }
        // Check for empty
        if (newFile.size === 0) {
            Sentry.captureMessage("Empty file", {level: "error", extra: { file: newFile.size }})
            return
        }
        setFile(newFile)
    }

    const renderLanding = () => {
        landingElements.forEach(id => {
            const e = document.getElementById(id)
            e!.style.display = ""
        })
    }

    const clearLandingScreen = () => {
        landingElements.forEach(id => {
            const e = document.getElementById(id)
            e!.style.display = "none"
        })
    }

    const uploadedDataSource = new UploadedDataSource()
    async function loadDataFromArgs() {
        if (location.pathname !== "/view") {
            appState !== AppState.INITIAL && setAppState(AppState.INITIAL)
            return
        }
        const args = getArguments(location, allLanguages)
        if (!args.sourceSpec) {
            navigate("/")
            return
        }
        if (appState === AppState.INITIAL || isNewData(args.sourceSpec, args.selection)) {
            try {
                setAppState(AppState.LOADING)
                setSourceSpec(args.sourceSpec)
                setFreezeAnimation(args.freezeAnimation)
                setConfig(args.config)
                const data = await uploadedDataSource.loadData({
                    spec: args.sourceSpec as UploadSourceSpec,
                    selection: args.selection,
                    allLanguages: allLanguages
                })
                sessionStorage.setItem("filename", getFilename(data.gedcom))
                setTopolaData(data)
                setSelection(args.selection !== undefined ? args.selection : startIndi(data))
                setAppState(AppState.SHOWING_CHART)
            } catch (error: any) {
                setAppState(AppState.ERROR)
            }
        } else if (appState === AppState.SHOWING_CHART || appState === AppState.LOADING) {
            setAppState(AppState.SHOWING_CHART)
            updateDisplay(args.selection !== undefined ? args.selection : startIndi(topolaData))
        }
    }

    function isNewData(newSourceSpec: DataSourceSpecs, newSelection?: IndiInfo) {
        if (!sourceSpec || sourceSpec.source !== newSourceSpec.source) return true  // New data source means new data
        const newSource = {spec: newSourceSpec, selection: newSelection}
        const oldSource = {spec: sourceSpec, selection: selection}
        return uploadedDataSource.isNewData(newSource as SourceSelection<UploadSourceSpec>, oldSource as SourceSelection<UploadSourceSpec>, topolaData)
    }

    function updateUrl(args: ParsedQuery<any>) {
        const search = queryString.parse(location.search)
        for (const key in args) search[key] = args[key]
        const path = location.pathname + "?" + queryString.stringify(search)
        navigate(path, { state: location.state })
    }

    /**
     * Sets the state with a new individual selection and chart type.
     */
    function updateDisplay(newSelection: IndiInfo) {
        if (!selection || selection.id !== newSelection.id || selection!.generation !== newSelection.generation)
            setSelection(newSelection)
    }

    const renderView = () => {
        switch (appState) {
            case AppState.LOADING:
            case AppState.SHOWING_CHART:
                if (!topolaData) return null
                clearLandingScreen()
                const updatedSelection = getSelection(topolaData!.chartData, selection)
                return (
                    <div id="content">
                        <TopolaChart
                            data={topolaData!.chartData}
                            selection={updatedSelection}
                            chartType={ChartType.Hourglass}
                            freezeAnimation={freezeAnimation}
                            colors={config.color}
                            selectedLanguage={config.selectedLanguage}
                            hideLanguages={LanguagesArg.SHOW}
                            hideEthnicity={EthnicityArg.SHOW}
                            hideIds={IdsArg.SHOW}
                            hideSex={SexArg.SHOW}
                            onSelection={(selection: IndiInfo) => updateUrl({
                                indi: selection.id,
                                gen: selection.generation
                            })}
                            languageOptions={config.languageOptions}
                        />
                    </div>
                )
            case AppState.ERROR:
                Sentry.captureMessage("Error during mobile /view render", { level: "error" })
        }
    }

    return (
        <>
            <ErrorBoundary key={errorBoundaryCheck.toString()} fallback={useMemo(() => <ErrorPage screenSize={ScreenSize.LARGE} onBackHome={() => {
                setErrorBoundaryCheck(false)
                navigate("/")
            }}/>, [navigate])}>

                {/* TOP MENU */}
                <TopMenu
                    appState={appState}
                    contactModalOpen={contactModalOpen}
                    pricingModalOpen={pricingModalOpen}
                    checkoutSuccess={checkoutSuccess}
                    privacyModalOpen={privacyModalOpen}
                    eventHandlers={{
                        onHome: () => { navigate("/"); renderLanding() },
                        setCheckoutSuccess,
                        setContactModalOpen,
                        setPricingModalOpen,
                        setPrivacyModalOpen,
                        onI18nLanguage
                    }}
                />

                {/* HEADER */}
                <div id="mobile-header" className="header-bg">
                    <Header className="landing-header" textAlign="center" as="h1">
                        <FormattedMessage id="landing.header.welcome"/>
                        <p style={{ fontWeight: "bold" }}><FormattedMessage id="landing.header.easy"/></p>
                    </Header>
                </div>

                <div id="landing-content">
                    {/* IMAGE */}
                    <img id="mobile-image" src="/assets/large-device.png" alt="You need a larger screen"/>

                    {/* LARGER DEVICE */}
                    <p id="mobile-text" className="larger-device"><FormattedMessage
                        id="landing.mobile.larger"
                        values={{
                            larger: <span style={{ fontWeight: "bold" }}><FormattedMessage id="landing.mobile.larger.device"/></span>,
                            break: <br/>
                        }}
                    />&nbsp;💻</p>

                    {/* UPLOAD FILE */}
                    <input type="file" accept={allowedExtensions.join(",")} ref={fileInputRef} onChange={handleFileChange} style={{ display: "none" }}/>
                    <Button id="upload-ged" onClick={() => fileInputRef.current?.click()}>
                        <Icon name="upload"/><FormattedMessage id="landing.column.dropzone.mobile"/>
                    </Button>
                </div>

                {/* ERROR BOUNDARY CHECKER */}
                <Button
                    id="error-boundary"
                    className={process.env.REACT_APP_SHOW_ERROR_BOUNDARY === "false" ? "hidden": ""}
                    onClick={() => setErrorBoundaryCheck(true)}>
                    <span>Check &lt;ErrorBoundary/&gt;</span>
                </Button>
                {errorBoundaryCheck && <ErrorBoundaryChecker/>}

                <Routes>
                    <Route path="/view" element={<>{renderView()}</>}/>
                    <Route path="/success" element={<CheckoutSuccess setCheckoutSuccess={setCheckoutSuccess} setPricingModalOpen={setPricingModalOpen}/>}/>
                    <Route path="/cancel" element={<CheckoutCancelled/>}/>
                    <Route path="*" element={<Navigate to="/" replace/>}/>
                </Routes>

            </ErrorBoundary>
        </>
    )
}